// Cloud REST integration for reconciliation + cloud playback (design spec §5.6).
//
// All cloud data for the reconciler comes through here:
//   - server events (GET /v3/devices/{id}/events) — reuses eventsLib.fetchDeviceEvents
//     (ascending + next_cursor; never bare apiFetch, which drops top-level meta) and
//     projects the NEW EventView fields (eid / record_location/mode/quality /
//     event_ts full-µs / snapshot_url) that the reconciler needs.
//   - cloud clip existence by TIME-RANGE (GET /v3/devices/{id}/recordings?from&to)
//     for cloudExists (no dedicated endpoint — overlap derivation, §4.3/§5.6).
//   - single-clip presign (GET /v3/recordings/{clip_id}/url) for cloud event playback.
//   - event snapshot presign (GET /v3/events/{eid}/snapshot) for View-1 thumbnails.
//
// These NEW EventView fields are optional on the wire until the Phase-4 backend lands;
// projection tolerates their absence so the cloud-only path keeps working.

import { apiFetch } from '../api';
import { fetchListWithMeta, fetchDeviceEvents, type EventItem, type ListResult } from '../../features/events/eventsLib';
import type { ServerEvent } from './reconcile';

const MAX_PAGES = 30;

/** Project a wire EventItem (+ optional NEW fields) into a reconciler ServerEvent. */
export function toServerEvent(deviceFallbackLoc: string, ev: EventItem): ServerEvent | null {
  const e = ev as EventItem & {
    eid?: string;
    event_ts_us?: number;
    record_location?: string;
    record_mode?: string;
    record_quality?: string;
    snapshot_url?: string;
  };
  // Without an eid the event cannot be keyed/deduped by identity. Fall back to a
  // synthesized key from correlation_id so cloud-only rows still render pre-eid wire.
  const eid = e.eid ?? (ev.correlation_id ? `corr:${ev.correlation_id}` : undefined);
  if (!eid) return null;
  return {
    eid,
    event_ts_us: typeof e.event_ts_us === 'number' && e.event_ts_us > 0 ? e.event_ts_us : ev.event_ts,
    event_type: ev.event_type,
    event_id_hex: ev.event_id,
    record_location: e.record_location ?? deviceFallbackLoc,
    record_mode: e.record_mode,
    record_quality: e.record_quality,
    snapshot_url: e.snapshot_url,
  };
}

/** Drain all server event pages in [fromUs,toUs) and project to ServerEvent[]. */
export async function fetchServerEvents(
  deviceId: string,
  fromUs: number,
  toUs: number,
  deviceFallbackLoc = 'cloud',
  signal?: AbortSignal
): Promise<ServerEvent[]> {
  const out: ServerEvent[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await fetchDeviceEvents(deviceId, { from: fromUs, to: toUs, cursor, limit: 100, signal });
    for (const ev of res.items) {
      const se = toServerEvent(deviceFallbackLoc, ev);
      if (se) out.push(se);
    }
    if (!res.nextCursor || res.items.length === 0) break;
    cursor = res.nextCursor;
  }
  return out;
}

/** A cloud recording clip span (recording_metadata projection). */
export interface CloudClip {
  clip_id: string;
  clip_start_ts: number;
  clip_end_ts: number;
  has_event?: boolean;
}

/**
 * GET /v3/devices/{id}/recordings?from&to — cloud clip spans for the cloudExists
 * time-range overlap. The recordings list is meta-paged via fetchListWithMeta.
 */
export async function fetchCloudClips(
  deviceId: string,
  fromUs: number,
  toUs: number,
  signal?: AbortSignal
): Promise<CloudClip[]> {
  const out: CloudClip[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res: ListResult<CloudClip> = await fetchListWithMeta<CloudClip>(
      `/v3/devices/${encodeURIComponent(deviceId)}/recordings`,
      { from: fromUs, to: toUs, limit: 200, cursor: cursor ?? undefined },
      signal
    );
    out.push(...res.items);
    if (!res.nextCursor || res.items.length === 0) break;
    cursor = res.nextCursor;
  }
  return out;
}

/** GET /v3/recordings/{clip_id}/url — presign for cloud event playback. */
export async function fetchCloudClipUrl(clipId: string, signal?: AbortSignal): Promise<string> {
  const res = await apiFetch<{ url?: string }>(`/v3/recordings/${encodeURIComponent(clipId)}/url`, { signal });
  const url = res.data?.url;
  if (!url) throw new Error('no presign url');
  return url;
}

/** GET /v3/events/{eid}/snapshot — presign for a cloud event thumbnail (NEW). */
export async function fetchEventSnapshotUrl(eid: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await apiFetch<{ url?: string }>(`/v3/events/${encodeURIComponent(eid)}/snapshot`, { signal });
    return res.data?.url ?? null;
  } catch {
    // Snapshot is best-effort (GAP-PB-2): absence falls back to a placeholder.
    return null;
  }
}
